import { useEffect, useState, type FC } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';
import * as Yup from 'yup';

import Auth from '../../../../api/auth';
import { FormModal } from '../../../../components/Formik';
import useApiQuery from '../../../../hooks/useApiQuery';

import type { APIHookArg } from '@/types';
import type { FieldConfig, FormikFormField } from '@/types/formTypes';
import type { Project } from '@/types/projectTypes';
import type { TechnicalLead } from '@/types/user';

interface EditProjectFormModalProps {
  project: Project;
  onSubmit: (data: unknown) => void;
  onCancel: () => void;
}

const EditProjectFormModal: FC<EditProjectFormModalProps> = ({ project, onSubmit, onCancel }) => {
  const intl = useIntl();
  const { projectId } = useParams<{ projectId: string }>();
  const [technicalLeads, setTechnicalLeads] = useState<TechnicalLead[] | null>(null);

  const { makeApiCall: getUsersCall } = useApiQuery<TechnicalLead[]>(Auth.fetchV2Users);
  const { makeApiCall: updateProjectCall } = useApiQuery(
    Auth.updateV2Project as unknown as APIHookArg,
    {
      successMessage: 'projectEdit',
    }
  );

  useEffect(() => {
    const getTechnicalLeads = async (): Promise<void> => {
      const data = await getUsersCall({
        organizationId: project.organizationId,
        roles: ['TECHNICAL_LEAD'],
      });

      if (data) {
        const activatedTechnicalLeads = data.filter(
          (technicalLead: TechnicalLead) => technicalLead.activated
        );
        setTechnicalLeads(activatedTechnicalLeads);
      }
    };

    getTechnicalLeads();
  }, [project.organizationId, getUsersCall, project]);

  const fields: FieldConfig[] = [
    {
      fieldType: 'text',
      name: 'description',
      label: intl.formatMessage({ id: 'description' }),
    },
    {
      fieldType: 'select',
      name: 'technicalLeadId',
      label: intl.formatMessage({ id: 'technicalLead' }),
      menuItems: technicalLeads?.map(({ _id, email }) => ({ value: _id, label: email })) ?? [],
    },
  ];

  const initialValues = {
    description: project.description,
    technicalLeadId: project.technicalLeadId,
  };

  const validationSchema = Yup.object().shape({
    description: Yup.string().required(intl.formatMessage({ id: 'formRequiredError' })),
  });

  const updateProject = async (formValues: Record<string, unknown>): Promise<void> => {
    onCancel();

    const data = await updateProjectCall({
      ...formValues,
      technicalLeadId: formValues.technicalLeadId ?? null,
      _id: projectId ?? project._id,
    });

    if (data) {
      onSubmit(data);
    }
  };

  return (
    <FormModal
      title={intl.formatMessage({ id: 'editProjectTitle' }, { name: project.name })}
      fields={fields as FormikFormField[]}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={formValues => {
        void updateProject(formValues);
      }}
      onCancel={onCancel}
      submitLabel={intl.formatMessage({ id: 'save' })}
    />
  );
};

export default EditProjectFormModal;
